// Copyright 2024 Sorama B.V.
import { Point3D } from "datatypes/SoramaLocalApi";
import { GenericMeasurement } from "datatypes/SoramaWebApi";
import { UnitSpectrumWithMeasurement } from "datatypes/WeightedSpectrumWithMeasurement";
import { IMeasurementProcessorResolver } from "services/Measurement/IMeasurementProcessorResolver";
import { ISpectralProcessingService } from "services/SoundProcessing/ISpectralProcessingService";
import { Frequency } from "units/Frequency";
import { Pressure } from "units/Pressure";
import { ISpectrumProvider } from "./ISpectrumProvider";
import { ComputationMode, ComputationModes } from "./SpectrumModuleModes";

export class FrequencySpectrumProvider implements ISpectrumProvider {
    constructor(private readonly processorResolver: IMeasurementProcessorResolver) {}

    public async getSpectra(
        measurement: GenericMeasurement,
        mic: Point3D,
        computationMode: ComputationMode,
        progressCallback: (progress: number) => void
    ): Promise<UnitSpectrumWithMeasurement> {
        const processor = await this.processorResolver.resolveProcessor<ISpectralProcessingService>(
            ISpectralProcessingService,
            measurement
        );

        const spectrumSet = await processor.getSpectrumSet(
            measurement,
            {
                duration: measurement.duration,
                timeOffsets: [0],
                useIV64Method: computationMode === ComputationModes.iV64,
            },
            progressCallback
        );

        const frequencies = spectrumSet.frequencies;
        const xUnit = Frequency.hertz;
        const yUnit = Pressure.pascal;
        const data = spectrumSet.results[0].data.map((value, index) => ({ x: frequencies[index], y: value }));
        const timeRange = spectrumSet.results[0].properties.timeRange;

        const unitSpectrumWithMeasurement = new UnitSpectrumWithMeasurement(xUnit, yUnit, data, timeRange);
        unitSpectrumWithMeasurement.measurement = measurement;
        unitSpectrumWithMeasurement.usedComputationMode = computationMode;

        return unitSpectrumWithMeasurement;
    }
}
